import { type SiteCreateInput, siteCreateSchema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../auth/store';
import { DemoPrefillButton } from '../../demo/components/DemoPrefillButton';
import { useCreateSite, useDeleteSite, useSites, useUpdateSite } from '../hooks/useSites';

interface SiteCardProps {
  index: number;
  defaultValues: Partial<SiteCreateInput>;
  isNew?: boolean;
  onSave: (data: SiteCreateInput) => Promise<void>;
  onCancel?: () => void;
  onDelete?: () => Promise<void>;
}

function SiteCard({ index, defaultValues, isNew, onSave, onCancel, onDelete }: SiteCardProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SiteCreateInput>({
    resolver: zodResolver(siteCreateSchema),
    defaultValues: {
      name: '',
      addressLine1: '',
      postalCode: '',
      city: '',
      country: '',
      ...defaultValues,
    },
  });

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/70 p-5 ring-1 ring-slate-100">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold tracking-tight text-slate-700">
          {isNew ? 'Nouveau site' : `Site ${index + 1}`}
        </p>
        {!isNew && onDelete && (
          <div className="flex items-center gap-2">
            {deleteConfirm ? (
              <>
                <span className="text-sm text-slate-600">Confirmer ?</span>
                <button
                  type="button"
                  className="rounded-lg border border-red-300 bg-red-50 px-3 py-1 text-sm font-semibold text-red-700 hover:bg-red-100"
                  onClick={onDelete}
                >
                  Oui
                </button>
                <button
                  type="button"
                  className="btn-muted !py-1 text-sm"
                  onClick={() => setDeleteConfirm(false)}
                >
                  Non
                </button>
              </>
            ) : (
              <button
                type="button"
                className="rounded-lg border border-red-200 px-3 py-1 text-sm font-semibold text-red-600 hover:bg-red-50"
                onClick={() => setDeleteConfirm(true)}
              >
                Supprimer
              </button>
            )}
          </div>
        )}
      </div>

      <form
        noValidate
        onSubmit={handleSubmit(async (data) => {
          await onSave(data);
          if (!isNew) setSaved(true);
        })}
      >
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="app-label" htmlFor={`site-${index}-name`}>
              Nom du site *
            </label>
            <input id={`site-${index}-name`} className="app-input" {...register('name')} />
            {errors.name && <p className="text-error mt-1.5">{errors.name.message}</p>}
          </div>
          <div>
            <label className="app-label" htmlFor={`site-${index}-address`}>
              Adresse
            </label>
            <input
              id={`site-${index}-address`}
              className="app-input"
              {...register('addressLine1')}
            />
          </div>
          <div>
            <label className="app-label" htmlFor={`site-${index}-postal`}>
              Code postal
            </label>
            <input id={`site-${index}-postal`} className="app-input" {...register('postalCode')} />
          </div>
          <div>
            <label className="app-label" htmlFor={`site-${index}-city`}>
              Ville
            </label>
            <input id={`site-${index}-city`} className="app-input" {...register('city')} />
          </div>
          <div>
            <label className="app-label" htmlFor={`site-${index}-country`}>
              Pays
            </label>
            <input id={`site-${index}-country`} className="app-input" {...register('country')} />
          </div>
        </div>

        {saved && !isDirty && (
          <p className="mt-3 text-[0.82rem] text-emerald-700">Modifications enregistrées.</p>
        )}

        <div className="mt-4 flex justify-end gap-3">
          {isNew && onCancel && (
            <button type="button" className="btn-muted" onClick={onCancel}>
              Annuler
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || (!isNew && !isDirty)}
          >
            {isSubmitting ? 'Enregistrement…' : isNew ? 'Créer le site' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
}

interface PendingNewSite {
  id: number;
  values: Partial<SiteCreateInput>;
}

export function ManageSitesPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin' || role === 'super_admin';

  const { data: sites, isLoading, error: loadError } = useSites();
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();

  const [pendingNew, setPendingNew] = useState<PendingNewSite[]>([]);
  const [nextId, setNextId] = useState(0);

  const addPending = (values: Partial<SiteCreateInput> = {}) => {
    setPendingNew((prev) => [...prev, { id: nextId, values }]);
    setNextId((n) => n + 1);
  };

  const removePending = (id: number) => {
    setPendingNew((prev) => prev.filter((p) => p.id !== id));
  };

  if (!isAdmin) return <Navigate to="/" />;

  if (isLoading)
    return (
      <div className="space-y-3">
        {(['sites-sk-1', 'sites-sk-2', 'sites-sk-3'] as const).map((key) => (
          <div key={key} className="h-36 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    );

  if (loadError)
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-red-900">
        Impossible de charger les sites.
      </div>
    );

  const handleDemoPrefill = (data: Record<string, unknown>) => {
    const incoming = data.sites as Partial<SiteCreateInput>[] | undefined;
    if (incoming && incoming.length > 0) {
      let currentNextId = nextId;
      const newPending: PendingNewSite[] = incoming.map((v) => ({
        id: currentNextId++,
        values: v,
      }));
      setPendingNew((prev) => [...prev, ...newPending]);
      setNextId(currentNextId);
    } else {
      addPending(data as Partial<SiteCreateInput>);
    }
  };

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xl">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-emerald-800/95">
            Mon entreprise
          </p>
          <h2 className="mt-3 text-[2.05rem] font-semibold tracking-tight text-slate-950 sm:text-[2.2rem] sm:leading-tight">
            Gérer les sites
          </h2>
          <p className="mt-3 text-[0.9rem] leading-relaxed text-slate-600">
            Consultez, modifiez ou ajoutez les sites de certification de votre entreprise.
          </p>
        </div>
        <DemoPrefillButton context="admin-sites" onPrefill={handleDemoPrefill} />
      </div>

      <div className="space-y-4">
        {sites?.map((site, idx) => (
          <SiteCard
            key={site.id}
            index={idx}
            defaultValues={{
              name: site.name,
              addressLine1: site.addressLine1 ?? '',
              postalCode: site.postalCode ?? '',
              city: site.city ?? '',
              country: site.country ?? '',
            }}
            onSave={async (data) => {
              await updateSite.mutateAsync({ id: site.id, data });
            }}
            onDelete={async () => {
              await deleteSite.mutateAsync(site.id);
            }}
          />
        ))}

        {sites?.length === 0 && pendingNew.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-8 py-12 text-center text-sm text-slate-500">
            Aucun site. Cliquez sur « Ajouter un site » pour commencer.
          </div>
        )}

        {pendingNew.map((pending, idx) => (
          <SiteCard
            key={pending.id}
            index={(sites?.length ?? 0) + idx}
            defaultValues={pending.values}
            isNew
            onSave={async (data) => {
              await createSite.mutateAsync(data);
              removePending(pending.id);
            }}
            onCancel={() => removePending(pending.id)}
          />
        ))}
      </div>

      <button type="button" className="btn-primary" onClick={() => addPending()}>
        Ajouter un site
      </button>
    </div>
  );
}
